import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { PagesService } from './pages.service';
import { SecureStorageService } from './secure-storage.service';

/**
 * A client outlet that can carry a QR code.
 *
 * Server shape:
 *   { id, created_at, clients_id, branchName, branchId }
 *
 * `branchId` is a UUID and is the value the QR link uses, while `id` is the
 * numeric row id. Field names have also appeared as `client_branches_id` and
 * `branch_name` elsewhere, so both spellings are read here.
 */
export interface ClientBranch {
  id: number;
  branchName: string;
  clientId: number;
  /** UUID used when building a QR link for this branch. */
  branchId: string;
  createdAt?: number;
  /** Untouched server row, for anything the UI has not modelled yet. */
  raw?: any;
}

/** A level inside a contest, as the QR registration form needs it. */
export interface QrLevel {
  id: number;
  name: string;
  /** `client_qr_completion` is the type that QR scanning completes. */
  levelType: string;
}

export interface QrContest {
  id: number;
  name: string;
  /** Kept so a registration's `contest_uid` can be shown as a campaign name. */
  uid?: string;
}

/**
 * One QR level this client has registered.
 *
 * Server shape (client_qr/my_registrations):
 *   { id, created_at, clientName, clientApiKey, client_id,
 *     contest_uid, level_uid }
 *
 * Note it identifies the campaign and level by UID, not by id or name — so
 * the list has no names of its own to show and resolves the campaign one
 * against the campaigns lookup where it can.
 */
export interface RegisteredQr {
  id: number;
  contestUid: string;
  levelUid: string;
  clientName: string;
  createdAt?: number;
  raw?: any;
}

@Injectable({ providedIn: 'root' })
export class BranchesService {
  private pagesService = inject(PagesService);
  private secureStorage = inject(SecureStorageService);

  /**
   * The client (company) id these branches belong to.
   *
   * The account's companies arrive on the login payload as `isCompany`. The
   * logged-in user id is NOT the client id, so it is deliberately not used as a
   * fallback — a wrong id would silently list another client's branches.
   */
  getCurrentClientId(): number {
    const user = this.secureStorage.getItem<any>('userDetails');
    const companies = user?.isCompany;
    if (Array.isArray(companies) && companies.length > 0) {
      const id = Number(companies[0]?.id);
      if (id > 0) return id;
    }
    const direct = Number(user?.client_id ?? user?.clients_id ?? 0);
    return direct > 0 ? direct : 0;
  }

  private normalize(row: any): ClientBranch {
    return {
      id: Number(row?.id ?? row?.client_branches_id ?? 0) || 0,
      branchName: String(row?.branchName ?? row?.branch_name ?? '').trim(),
      clientId: Number(row?.clients_id ?? row?.client_id ?? 0) || 0,
      branchId: String(row?.branchId ?? row?.branch_id ?? '').trim(),
      createdAt: Number(row?.created_at) || undefined,
      raw: row,
    };
  }

  /**
   * GET /client_branches?client_id=<id>
   *
   * Without the query parameter the endpoint returns EVERY client's branches,
   * so the id is always sent. The client-side filter below is a second guard in
   * case the parameter is ever ignored.
   */
  getBranches(clientId?: number): Observable<ClientBranch[]> {
    const id = clientId && clientId > 0 ? clientId : this.getCurrentClientId();
    const params = id > 0 ? { client_id: id } : undefined;

    return this.pagesService.getAll('CLIENT_BRANCHES', params).pipe(
      timeout(10000),
      map((res: any) => {
        const list = Array.isArray(res)
          ? res
          : (res?.data || res?.items || res?.result || res?.branches || []);

        const rows = (Array.isArray(list) ? list : []).map((row: any) => this.normalize(row));
        return id > 0 ? rows.filter((b) => b.clientId === id) : rows;
      })
    );
  }

  /**
   * GET /client_qr/my_registrations
   *
   * The QR levels this client has already registered. "my_registrations"
   * scopes itself to the caller's token, so unlike getBranches() there is no
   * client id to send.
   */
  getRegisteredQrs(): Observable<RegisteredQr[]> {
    return this.pagesService.getAll('CLIENT_QR_MY_REGISTRATIONS').pipe(
      timeout(10000),
      map((res: any) => {
        const list = Array.isArray(res)
          ? res
          : (res?.data || res?.items || res?.result || res?.registrations || []);
        return (Array.isArray(list) ? list : []).map((row: any) => this.normalizeRegisteredQr(row));
      })
    );
  }

  private normalizeRegisteredQr(row: any): RegisteredQr {
    return {
      id: Number(row?.id ?? 0) || 0,
      contestUid: String(row?.contest_uid ?? row?.contestUid ?? '').trim(),
      levelUid: String(row?.level_uid ?? row?.levelUid ?? '').trim(),
      clientName: String(row?.clientName ?? row?.client_name ?? '').trim(),
      createdAt: Number(row?.created_at) || undefined,
      raw: row,
    };
  }

  /**
   * Create and update read DIFFERENT field names on the same table:
   *   POST wants  { client_id,  branch_name }
   *   PUT  wants  { clients_id, branchName }
   *
   * Sending the wrong pair silently writes blanks — a PUT with `client_id` set
   * the row's client to 0 and a PUT with `branch_name` blanked the name, both
   * while returning 200. Unknown keys are ignored, so every call sends all four
   * spellings and each endpoint picks the pair it understands.
   */
  private buildPayload(clientId: number, branchName: string) {
    const name = branchName.trim();
    return {
      client_id: clientId,
      clients_id: clientId,
      branch_name: name,
      branchName: name,
    };
  }

  /**
   * POST /client_branches on the KVYQ1PW2 group.
   *
   * Only the name is sent: this endpoint resolves the client from the auth
   * token, so no client id (and no API key) leaves the browser. It also needs
   * the X-Data-Source header, which the interceptor still adds for this group.
   */
  createBranch(branchName: string, _clientId?: number): Observable<any> {
    const payload = { branchName: branchName.trim() };
    console.log('[BranchesService] POST /client_branches payload:', payload);
    return this.pagesService.create('CLIENT_BRANCHES_CREATE', payload).pipe(timeout(10000));
  }

  /**
   * PUT /client_branches/{id}
   *
   * POST does NOT update: sending an `id` to it creates another row, so edits
   * must go through PUT. PATCH is not published (404).
   */
  updateBranch(id: number, branchName: string, clientId?: number): Observable<any> {
    const client = clientId && clientId > 0 ? clientId : this.getCurrentClientId();
    const payload = this.buildPayload(client, branchName);
    console.log(`[BranchesService] PUT /client_branches/${id} payload:`, payload);
    return this.pagesService.putById('CLIENT_BRANCHES', id, payload).pipe(timeout(10000));
  }

  /** DELETE /client_branches/{id} — the id goes in the path, not the body. */
  deleteBranch(id: number): Observable<any> {
    console.log(`[BranchesService] DELETE /client_branches/${id}`);
    return this.pagesService.deleteCard('CLIENT_BRANCHES', id).pipe(timeout(10000));
  }

  /**
   * Campaign list for the QR registration form.
   *
   * This endpoint returns id and name only — levels are NOT included and its
   * `contest_id` filter is ignored — so levels are fetched per campaign by
   * getContestLevels() once one is chosen.
   */
  getContests(): Observable<QrContest[]> {
    return this.pagesService.getAll('GET_DASHBOARD_CONTESTS', { contest_id: 0 }).pipe(
      timeout(15000),
      map((res: any) => {
        const rows = Array.isArray(res) ? res : (res?.items || res?.data || res?.result || []);
        return (Array.isArray(rows) ? rows : [])
          .map((c: any) => ({
            id: Number(c?.id) || 0,
            name: String(c?.name || '').trim() || `Campaign #${c?.id}`,
            uid: String(c?.uid ?? c?.contest_uid ?? '').trim(),
          }))
          .filter((c: QrContest) => c.id > 0);
      })
    );
  }

  /**
   * GET /contest/{id} — the only response that carries `_levels_of_contest`,
   * which holds the numeric level ids the register call needs.
   */
  getContestLevels(contestId: number): Observable<QrLevel[]> {
    return this.pagesService.getById('GET_SINGLE_CONTEST', contestId).pipe(
      timeout(15000),
      map((res: any) => {
        const contest = Array.isArray(res) ? res[0] : (res?.data || res);
        const levels = Array.isArray(contest?._levels_of_contest)
          ? contest._levels_of_contest
          : (Array.isArray(contest?.levels_of_contest) ? contest.levels_of_contest : []);
        return levels
          .map((l: any) => ({
            id: Number(l?.id) || 0,
            name: String(l?.name || '').trim() || `Level #${l?.id}`,
            levelType: String(l?.level_type || '').trim(),
          }))
          .filter((l: QrLevel) => l.id > 0);
      })
    );
  }

  /**
   * POST /client_qr_register — body is { contest_id, level_id }.
   * The client is taken from the token, so nothing else is sent.
   */
  registerQr(contestId: number, levelId: number): Observable<any> {
    const payload = { contest_id: Number(contestId), level_id: Number(levelId) };
    console.log('[BranchesService] POST /client_qr_register payload:', payload);
    return this.pagesService.create('CLIENT_QR_REGISTER', payload).pipe(timeout(15000));
  }

  /**
   * GET /client_api_key
   *
   * The key has turned up under eight different property names across
   * environments, so all of them are checked. A failure returns an empty string
   * rather than throwing, so the screen still renders.
   */
  getApiKey(): Observable<string> {
    return this.pagesService.getAll('CLIENT_API_KEY').pipe(
      timeout(10000),
      map((res: any) => {
        const key =
          res?.clientApiKey ?? res?.api_key ?? res?.apikey ?? res?.apiKey ??
          res?.data?.clientApiKey ?? res?.data?.api_key ??
          res?.data?.apikey ?? res?.data?.apiKey ?? '';
        return String(key || '').trim();
      }),
      catchError((err) => {
        console.warn('[BranchesService] Failed to load the client API key:', err);
        return of('');
      })
    );
  }
}
